import * as React from 'react';
import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { QuestionLayoutContainer } from '../QuestionLayoutContainer';
import { path, pathOr } from 'ramda';
import { useSimpleForm } from '../../utils/useSimpleForm';
import { isEmailValid } from './utils';
import { pickOnlyMeaning } from '../../utils/pickOnlyMeaning';
import { useFormTracking } from '../../utils/useFormTracking';
import { FormInputsLayout } from '../../components/FormInputsLayout';

export const DEFAULT_EMAIL_PLACEHOLDER = 'Введите email';

export const EmailQuestionContainer: React.FC<IBaseQuestionProps> = props => {
  const {
    question: { answerField, placeholder, title, required },
    answersState,
    setNextStep,
    setAnswersState,
  } = props;

  const { trackSubmit, trackChange } = useFormTracking();

  const { handleSubmit, handleChange: onChange, values, errors, isFormValid } = useSimpleForm({
    onSubmit(values) {
      setAnswersState(prevState => {
        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: {
            answerField,
            value: values[answerField],
          },
        };

        setNextStep(nextAnswersState);
        trackSubmit({
          isRequired: required,
          field: answerField,
          value: null,
        });

        return nextAnswersState;
      });
    },
    validators: {
      [answerField]: value =>
        (!value && !required) || isEmailValid(value as string) ? null : 'Введите корректный email',
    },
    initialValues: pickOnlyMeaning({
      [answerField]: path([answerField, 'value'], answersState),
    }),
  });

  const handleChange = React.useCallback<NonNullable<React.ComponentProps<'input'>['onChange']>>(
    e => {
      trackChange();

      onChange(answerField, e.target.value);
    },
    [onChange, answerField, trackChange],
  );

  return (
    <QuestionLayoutContainer title={title} onSubmit={handleSubmit} disableSubmit={!isFormValid}>
      <FormInputsLayout
        InputProps={{
          type: 'text',
          name: answerField,
          invalid: !!errors[answerField],
          errorMessage: errors[answerField],
          placeholder: placeholder || DEFAULT_EMAIL_PLACEHOLDER,
          onChange: handleChange,
          value: pathOr('', [answerField], values),
          autoCapitalize: 'none',
        }}
      />
    </QuestionLayoutContainer>
  );
};

EmailQuestionContainer.displayName = 'EmailQuestionContainer';
